import { ApiCallError, LocalCliError } from "./errors.js";
import { resolveBackendHint, resolveLocalHint } from "./error-hints.js";
import { colors, renderJson, resolveOutputMode, type OutputMode } from "./output.js";
import { classifyFoundryError } from "@m8t-stack/foundry-invoke";

export interface RenderErrorOptions {
  stderr: NodeJS.WriteStream;
  output?: OutputMode;
  verbose?: boolean;
}

/**
 * Render an unexpected error onto stderr and return the appropriate exit code.
 * - `1`: every backend or local failure
 * - `2`: user-cancelled (recognized by `error.name === "ExitPromptError"` from @inquirer/prompts)
 */
/**
 * Lay a hint out under the `  hint: ` label. A multi-line hint's continuation
 * lines are indented to that label's width so they read as one block.
 *
 * The indent lives HERE, not in the hint strings: `--output json` emits the
 * hint verbatim, and padding baked into the string would ship display
 * whitespace to machine consumers.
 */
function renderHint(hint: string): string {
  return hint.split("\n").join("\n        ");
}

export function renderError(err: unknown, opts: RenderErrorOptions): number {
  const mode = resolveOutputMode(opts.output, opts.stderr);

  // User-cancelled (@inquirer/prompts.select / confirm)
  if (
    err instanceof Error &&
    (err.name === "ExitPromptError" || err.message === "User force closed the prompt")
  ) {
    if (mode === "json") {
      opts.stderr.write(
        renderJson({
          ok: false,
          error: { code: "CANCELLED", message: "User cancelled the operation." },
        }) + "\n",
      );
    } else {
      opts.stderr.write("cancelled.\n");
    }
    return 2;
  }

  if (err instanceof ApiCallError) {
    if (mode === "json") {
      opts.stderr.write(renderJson({ ok: false, error: err.envelope }) + "\n");
      return 1;
    }
    opts.stderr.write(`${colors.error("error:")} ${err.envelope.message}\n`);
    const hint = resolveBackendHint(err.envelope);
    if (hint) opts.stderr.write(`  ${colors.hint("hint:")} ${renderHint(hint)}\n`);
    if (opts.verbose) {
      opts.stderr.write("\n" + colors.dim(renderJson(err.envelope)) + "\n");
    }
    return 1;
  }

  if (err instanceof LocalCliError) {
    if (mode === "json") {
      const cls = classifyFoundryError(err);
      opts.stderr.write(
        renderJson({
          ok: false,
          error: {
            code: err.code,
            message: err.message,
            category: err.category ?? cls.category,
            retryable: err.retryable ?? cls.retryable,
            ...(err.hint ? { details: { hint: err.hint } } : {}),
          },
        }) + "\n",
      );
      return 1;
    }
    opts.stderr.write(`${colors.error("error:")} ${err.message}\n`);
    const hint = err.hint ?? resolveLocalHint(err.code);
    if (hint) opts.stderr.write(`  ${colors.hint("hint:")} ${renderHint(hint)}\n`);
    if (opts.verbose && err.cause instanceof Error) {
      opts.stderr.write("\n" + colors.dim(`caused by: ${err.cause.stack ?? err.cause.message}`) + "\n");
    }
    return 1;
  }

  // Unknown error — print message + stack (only on verbose)
  const e = err instanceof Error ? err : new Error(String(err));
  if (mode === "json") {
    opts.stderr.write(
      renderJson({
        ok: false,
        error: { code: "UNEXPECTED", message: e.message },
      }) + "\n",
    );
    return 1;
  }
  opts.stderr.write(`${colors.error("error:")} ${e.message}\n`);
  if (opts.verbose && e.stack) {
    opts.stderr.write("\n" + colors.dim(e.stack) + "\n");
  } else {
    opts.stderr.write(`  ${colors.hint("hint:")} run with --verbose for the stack trace.\n`);
  }
  return 1;
}
